import React from "react";
import { RCCGrid } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import { ModuleTypes } from "../../interface";
import BadgeMaps from "./badgeMaps";

export interface IAssociate{
	associate: number;
	delayed: number;
	percentage: number;
	color?: string;
	bond?: number;
	id?: number;
}

export interface IAssociations{
	action: IAssociate;
	indicator: IAssociate;
	project: IAssociate;
	strategy: IAssociate;
	swot: IAssociate;
}

export interface IBadgeAssociate{
	mapId?: number;
	perspectiveId?: number;
	objectiveId?: number;
    associations?: IAssociations;
	position: "center" | "right" | "left";
	label: "word" | "letter";
	color?: "grey" | "purple" | "blue" | "red" | "yellow" | "green" | "black" | "orange" | "main";
}

const BadgeAssociate: React.FC<IBadgeAssociate> = ({
	mapId,
	perspectiveId,
	objectiveId,
	associations,
	position,
	label,
	color
}) => {
	const { translate, currentLanguage } = useTranslation();

	const createTooltip = (elem: IAssociate, singularAssociate: string, pluralAssociate: string, singularDelayedOrBond: string, pluralDelayedOrBond: string) => {
		let tooltip = "";

		if(elem.associate === 1){
			tooltip+= `${elem.associate} ${singularAssociate}`;
		}else{
			tooltip+= `${elem.associate} ${pluralAssociate}`;
		}

		if(elem.delayed !== 0){
			if(elem.delayed === 1){
				tooltip+= `, ${elem.delayed} ${singularDelayedOrBond}`;
			}else{
				tooltip+= `, ${elem.delayed} ${pluralDelayedOrBond}`;
			}
		}

		if(elem.bond){
			if(elem.delayed === 1){
				tooltip+= `, ${elem.bond} ${singularDelayedOrBond}`;
			}else{
				tooltip+= `, ${elem.bond} ${pluralDelayedOrBond}`;
			}
		}
		
		return tooltip;
	};

	const createTooltipAssociate = (type: ModuleTypes, associate: IAssociate) => {
		switch (type) {
		case ModuleTypes.ACTIONS:
			return createTooltip(associate, translate("ação associada"), translate("ações associadas"), translate("está atrasada"), translate("estão atrasadas"));

		case ModuleTypes.INDICATORS:
			return createTooltip(associate, translate("indicador associado"), translate("indicadores associados"), translate("está atrasado"), translate("estão atrasados"));

		case ModuleTypes.PROJECTS:
			return createTooltip(associate, translate("projeto associado"), translate("projetos associados"), translate("está atrasado"), translate("estão atrasados"));	
		
		case ModuleTypes.SWOT:
			return createTooltip(associate, translate("item da SWOT associada"), translate("itens da SWOT associadas"), translate("vínculo de item da SWOT associado"), translate("vínculos de itens da SWOT associados"));
			
		case ModuleTypes.STRATEGY:
			return createTooltip(associate, translate("estratégia associada"), translate("estratégias associadas"), translate("está atrasada"), translate("estão atrasadas"));

		default:
			return "";
		}
	};

	const getPosition = () => {

		switch (position) {
		case "center":
			return "center";
		case "left":
			return "flex-start";
		case "right":
			return "flex-end";
		default:
			return "center";
		}
	};

	const labelWord = (wordSingular: string, wordPlural: string, associate: number) => (
		<>
			<span>{associate} {associate === 1 ? wordSingular : wordPlural}</span>
		</>
	);

	const createLabelWord = (type: ModuleTypes, associate: number) => {
		switch (type) {
		case ModuleTypes.ACTIONS:
			return labelWord(translate("Ação"), translate("Ações"), associate);

		case ModuleTypes.INDICATORS:
			return labelWord(translate("Indicador"), translate("Indicadores"), associate);

		case ModuleTypes.PROJECTS:
			return labelWord(translate("Projeto"), translate("Projetos"), associate);	
		
		case ModuleTypes.SWOT:
			return labelWord(translate("SWOT"), translate("SWOT"), associate);
			
		case ModuleTypes.STRATEGY:
			return labelWord(translate("Estratégia"), translate("Estratégias"), associate);

		default:
			return (<></>);
		}
	};

	const createLabelLetter = (type: ModuleTypes) => {
		switch (type) {
		case ModuleTypes.ACTIONS:
			return (<><span>A</span></>);

		case ModuleTypes.INDICATORS:
			return (<span>I</span>);

		case ModuleTypes.PROJECTS:
			return (<span>P</span>);

		case ModuleTypes.SWOT:
			return (<span>{currentLanguage === "en" ? "SW" : "S"}</span>);
			
		case ModuleTypes.STRATEGY:
			return (<span>{currentLanguage === "en" ? "ST" : "E"}</span>);

		default:
			return "";
		}
	};

	const createLink = (type: ModuleTypes, id?: number) => {
		switch (type) {
		case ModuleTypes.ACTIONS:
			return () => window.open(`/actions/panel/?group=3&map=${mapId || ""}&perspective=${perspectiveId || ""}&objective=${objectiveId || ""}`, "_blank");

		case ModuleTypes.INDICATORS:
			return () =>  window.open(`/indicators/panel/?group=3&map=${mapId || ""}&perspective=${perspectiveId || ""}&objective=${objectiveId || ""}`, "_blank");

		case ModuleTypes.PROJECTS:
			return () => window.open(`/projects/panel/?group=3&map=${mapId || ""}&perspective=${perspectiveId || ""}&objective=${objectiveId || ""}`, "_blank");

		case ModuleTypes.SWOT:
			return () => window.open(`/analysis/swot/${id}?map=${mapId || ""}&perspective=${perspectiveId || ""}&objective=${objectiveId || ""}`, "_blank");
			
		case ModuleTypes.STRATEGY:
			return () => window.open(`/strategies/panel/?map=${mapId || ""}&perspective=${perspectiveId || ""}&objective=${objectiveId || ""}`, "_blank");

		default:
			return undefined;
		}
	};

	return(
		<>
			{
				associations && 
				<RCCGrid
					container
					justifyContent={getPosition()}
				>
					{
						associations.action.associate !== 0 && 
						<BadgeMaps
							label={label==="word" ? 
								createLabelWord(ModuleTypes.ACTIONS, associations.action.associate) : createLabelLetter(ModuleTypes.ACTIONS)}
							tooltip={createTooltipAssociate(ModuleTypes.ACTIONS, associations.action)}
							color={associations.action.delayed !== 0 ? "red" : color || undefined}
							onClick={createLink(ModuleTypes.ACTIONS)}
						/>
					}
					{
						associations.project.associate !== 0 &&
						<BadgeMaps
							label={label==="word" ? 
								createLabelWord(ModuleTypes.PROJECTS, associations.project.associate) : createLabelLetter(ModuleTypes.PROJECTS)}
							tooltip={createTooltipAssociate(ModuleTypes.PROJECTS, associations.project)}
							color={associations.project.delayed !== 0 ? "red" : color || undefined}
							onClick={createLink(ModuleTypes.PROJECTS)}
						/>
					}
					{
						associations.indicator.associate !== 0 &&
						<BadgeMaps
							label={label==="word" ? 
								createLabelWord(ModuleTypes.INDICATORS, associations.indicator.associate) : createLabelLetter(ModuleTypes.INDICATORS)}
							tooltip={createTooltipAssociate(ModuleTypes.INDICATORS, associations.indicator)}
							color={associations.indicator.delayed !== 0 ? "red" : color || undefined}
							onClick={createLink(ModuleTypes.INDICATORS)}
						/>
					}
					{
						associations.strategy.associate !== 0 &&
						<BadgeMaps
							label={label==="word" ? 
								createLabelWord(ModuleTypes.STRATEGY, associations.strategy.associate) : createLabelLetter(ModuleTypes.STRATEGY)}
							tooltip={createTooltipAssociate(ModuleTypes.STRATEGY, associations.strategy)}
							color={associations.strategy.delayed !== 0 ? "red" : color || undefined}
							onClick={createLink(ModuleTypes.STRATEGY)}
						/>
					}
					{
						(associations.swot.bond || associations.swot.associate !== 0) &&
						<BadgeMaps
							label={label==="word" ? 
								createLabelWord(ModuleTypes.SWOT, associations.swot.associate) : createLabelLetter(ModuleTypes.SWOT)}
							tooltip={createTooltipAssociate(ModuleTypes.SWOT, associations.swot)}
							color={associations.swot.delayed !== 0 ? "red" : color || undefined}
							onClick={associations.swot.bond ? createLink(ModuleTypes.SWOT, associations.swot.id || 0) : undefined}
						/>
					}
				</RCCGrid>
			}
		</>
	);
};

export default BadgeAssociate;
